import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    // Vérifier si l'utilisateur est déjà connecté (localStorage)
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });

  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      console.log('🔄 AuthContext: checkAuth started');
      
      const token = localStorage.getItem('auth_token');
      const savedUser = localStorage.getItem('user');

      // 1. Restaure d'abord depuis localStorage
      if (savedUser) {
        try {
          const parsedUser = JSON.parse(savedUser);
          console.log('📦 User restauré depuis localStorage:', parsedUser);
          setUser(parsedUser);
        } catch (e) {
          console.error('❌ Erreur parsing saved user:', e);
        }
      }
      
      // 2. Si pas de token, termine
      if (!token) {
        console.log('🔓 Pas de token, utilisateur non connecté');
        setLoading(false);
        setInitialized(true);
        return;
      }
      
      try {
        console.log('🔐 Token présent, vérification avec API...');
        
        // ⭐⭐ CHANGE ICI : utilise /auth/me au lieu de /me ⭐⭐
        const response = await api.get('/auth/me');
        console.log('✅ Réponse /auth/me:', response.data);
        
        // Vérifie le format de la réponse
        if (response.data.success && response.data.user) {
          console.log('👤 Données utilisateur mises à jour:', response.data.user);
          setUser(response.data.user);
          // Met à jour localStorage
          localStorage.setItem('user', JSON.stringify(response.data.user));
        } else {
          console.warn('⚠️ Réponse API format inattendu:', response.data);
          // Ne nettoie pas, garde l'utilisateur en cache
        }
        
      } catch (error) {
        console.error('❌ Erreur vérification auth:', error);
        console.error('Détails:', {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message
        });
        
        // ⭐ IMPORTANT : Nettoyage SEULEMENT si erreur 401 (Unauthorized)
        if (error.response?.status === 401) {
          console.log('🔒 Token expiré ou invalide, nettoyage...');
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
          setUser(null);
        }
        // Pour les autres erreurs (500, network), garde l'utilisateur en cache
      } finally {
        console.log('🏁 Initialisation auth terminée');
        setLoading(false);
        setInitialized(true);
      }
    };
    
    checkAuth();
  }, []);

  const login = async (username, password) => {
    try {
      setLoading(true);
      console.log('🔐 Tentative de connexion pour:', username);
      
      // 1. Récupère le cookie CSRF
      await api.get('/sanctum/csrf-cookie');
      
      // 2. Appelle l'API Laravel
      const response = await api.post('/auth/login', {
        login: username,
        password: password
      });
      
      console.log('✅ Login API response:', response.data);
      
      // 3. Stocke le token
      localStorage.setItem('auth_token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // 4. Met à jour l'état global
      setUser(response.data.user);
      
      return { 
        success: true, 
        user: response.data.user 
      };
      
    } catch (error) {
      console.error('❌ Login error:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = 'Erreur de connexion';
      
      if (error.response?.status === 422) {
        errorMessage = 'Données invalides';
      } else if (error.response?.status === 401) {
        errorMessage = 'Identifiants incorrects';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message === 'Network Error') {
        errorMessage = 'Impossible de joindre le serveur';
      }
      
      return { 
        success: false, 
        message: errorMessage 
      };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        await api.post('/auth/logout');
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      setUser(null);
      console.log('👋 Utilisateur déconnecté');
    }
  };

  const isAuthenticated = () => {
    // Vérifie à la fois le token et l'utilisateur
    const token = localStorage.getItem('auth_token');
    const savedUser = localStorage.getItem('user');
    return !!(token && savedUser && user);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        loading,
        initialized,
        isAuthenticated: isAuthenticated(), // ✅ Retourne un booléen
        isAdmin: !!user,
        isMainAdmin: user?.role?.name === 'admin',
        isSubAdmin: user?.role?.name === 'sous_admin',
        isSupAdmin: user?.role?.name === 'operationnel'
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// import React, { createContext, useContext, useState, useEffect } from 'react';
// import { authService } from '../services/authService';
// import api from '../services/api';

// const AuthContext = createContext();

// // 🔐 IDENTIFIANTS ADMIN HARDCODÉS
// // const ADMIN_CREDENTIALS = {
// //   admin: {
// //     username: 'admin',
// //     password: 'Admin@2024',
// //     role: 'admin'
// //   },
// //   subadmin: {
// //     username: 'subadmin',
// //     password: 'SubAdmin@2024',
// //     role: 'subadmin'
// //   }
// // };

// export const useAuth = () => {
//   const context = useContext(AuthContext);
//   if (!context) {
//     throw new Error('useAuth doit être utilisé dans un AuthProvider');
//   }
//   return context;
// };

// export const AuthProvider = ({ children }) => {
//   const [user, setUser] = useState(() => {
//     // Vérifier si l'utilisateur est déjà connecté (localStorage)
//     const savedUser = localStorage.getItem('user');
//     return savedUser ? JSON.parse(savedUser) : null;
//   });

//   const [loading, setLoading] = useState(true);
//   const [initialized, setInitialized] = useState(false); // ⭐ AJOUTE CETTE LIGNE

//   useEffect(() => {
//     const checkAuth = async () => {
//       const token = localStorage.getItem('auth_token');
//       const savedUser = localStorage.getItem('user');

//       if (savedUser) {
//         try {
//           setUser(JSON.parse(savedUser));
//         } catch (e) {
//           console.error('Erreur parsing saved user:', e);
//         }
//       }
      
//       if (!token) {
//         setLoading(false);
//         setInitialized(true); // ⭐ AJOUTE ICI
//         return;
//       }
      
//       try {
//         // Vérifie si le token est toujours valide
//         const response = await api.get('/me');
//         setUser(response.data);
//       } catch (error) {
//         console.error('Auth check failed:', error);
//         // Token invalide, nettoie
//         localStorage.removeItem('auth_token');
//         localStorage.removeItem('user');
//         setUser(null);
//       } finally {
//         setLoading(false);
//         setInitialized(true); // ⭐ IMPORTANT : marque que l'init est finie
//       }
//     };
    
//     checkAuth();
//   }, []);

//   const login = async (username, password) => {
//     try {
//       setLoading(true);
      
//       // 1. Récupère le cookie CSRF
//       await api.get('/sanctum/csrf-cookie');
      
//       // 2. Appelle l'API Laravel
//       const response = await api.post('/auth/login', { // ⭐ NOTE: pas /api/ ici car baseURL l'a déjà
//         login: username,
//         password: password
//       });
      
//       console.log('✅ Login API response:', response.data); // ⭐ DEBUG
      
//       // 3. Stocke le token
//       localStorage.setItem('auth_token', response.data.access_token);
//       localStorage.setItem('user', JSON.stringify(response.data.user));
      
//       // 4. Met à jour l'état global
//       setUser(response.data.user);
      
//       return { 
//         success: true, 
//         user: response.data.user 
//       };
      
//     } catch (error) {
//       console.error('❌ Login error:', error);
//       console.error('Error response:', error.response?.data); // ⭐ DEBUG
      
//       // Message d'erreur détaillé
//       let errorMessage = 'Erreur de connexion';
      
//       if (error.response?.status === 422) {
//         errorMessage = 'Données invalides';
//       } else if (error.response?.status === 401) {
//         errorMessage = 'Identifiants incorrects';
//       } else if (error.response?.data?.message) {
//         errorMessage = error.response.data.message;
//       } else if (error.message === 'Network Error') {
//         errorMessage = 'Impossible de joindre le serveur';
//       }
      
//       return { 
//         success: false, 
//         message: errorMessage 
//       };
//     } finally {
//       setLoading(false);
//     }
//   };

//   // const login = async (username, password) => {
//   //   try {
//   //     // 1. Récupère le cookie CSRF
//   //     await api.get('/sanctum/csrf-cookie');
      
//   //     // 2. Appelle l'API Laravel
//   //     const response = await api.post('/api/auth/login', {
//   //       login: username,     // ⭐ IMPORTANT : ton API attend "login"
//   //       password: password
//   //     });
      
//   //     // 3. Stocke le token
//   //     localStorage.setItem('auth_token', response.data.access_token);
//   //     localStorage.setItem('user', JSON.stringify(response.data.user));
      
//   //     // 4. Met à jour l'état global
//   //     setUser(response.data.user);
      
//   //     return { success: true, user: response.data.user };
//   //   } catch (error) {
//   //     return { 
//   //       success: false, 
//   //       message: error.response?.data?.message || 'Erreur de connexion' 
//   //     };
//   //   }
//   // };

//   const logout = async () => {
//     try {
//       // Appelle l'API logout si token existe
//       const token = localStorage.getItem('auth_token');
//       if (token) {
//         await api.post('/auth/logout');
//       }
//     } catch (error) {
//       console.error('Logout error:', error);
//     } finally {
//       // Nettoie TOUJOURS le localStorage
//       localStorage.removeItem('auth_token');
//       localStorage.removeItem('user');
//       setUser(null);
//     }
//   };

//   return (
//     <AuthContext.Provider
//       value={{
//         user,
//         login,
//         logout,
//         loading,
//         initialized,
//         isAdmin: !!user,
//         isMainAdmin: user?.role?.name === 'admin',
//         isSubAdmin: user?.role?.name === 'subadmin'
//       }}
//     >
//       {children}
//     </AuthContext.Provider>
//   );
// };